import React, { useEffect, useRef, useState } from 'react'

interface Props {
  value: string
  onChange: (v: string) => void
  options: string[]
  placeholder?: string
  id?: string
}

// Score how well `text` matches the lowercased query `q`. Higher is better;
// 0 means no match. Ranking: exact > prefix > word-boundary substring >
// mid-word substring > scattered subsequence (fuzzy). This lets "bila" find
// "Billa", "drog" find "dm drogerie markt GmbH", etc.
function scoreMatch(text: string, q: string): number {
  if (text === q) return 1000
  const idx = text.indexOf(q)
  if (idx === 0) return 900 - text.length
  if (idx > 0) {
    const wordStart = /\s/.test(text[idx - 1])
    return (wordStart ? 700 : 500) - idx
  }
  // Subsequence: every query char appears in order, scored by how tight the run is.
  let ti = 0, qi = 0, gaps = 0, first = -1, last = -1
  while (ti < text.length && qi < q.length) {
    if (text[ti] === q[qi]) {
      if (first < 0) first = ti
      if (last >= 0 && ti - last > 1) gaps += ti - last - 1
      last = ti
      qi++
    }
    ti++
  }
  if (qi < q.length) return 0
  return Math.max(40, 300 - gaps * 6 - first)
}

function rank(options: string[], query: string): string[] {
  const q = query.trim().toLowerCase()
  if (!q) return [...options].sort((a, b) => a.localeCompare(b))
  return options
    .map((s) => ({ s, score: scoreMatch(s.toLowerCase(), q) }))
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score || a.s.localeCompare(b.s))
    .map((x) => x.s)
}

// Bold the characters of `s` that matched the query (subsequence positions).
function highlight(s: string, query: string): React.ReactNode {
  const q = query.trim().toLowerCase()
  if (!q) return s
  const lower = s.toLowerCase()
  const out: React.ReactNode[] = []
  let qi = 0
  let run = ''
  let mark = ''
  const flush = () => {
    if (run) { out.push(run); run = '' }
    if (mark) { out.push(<mark key={out.length}>{mark}</mark>); mark = '' }
  }
  for (let i = 0; i < s.length; i++) {
    if (qi < q.length && lower[i] === q[qi]) {
      if (run) { out.push(run); run = '' }
      mark += s[i]
      qi++
    } else {
      if (mark) { out.push(<mark key={out.length}>{mark}</mark>); mark = '' }
      run += s[i]
    }
  }
  flush()
  return out
}

const StoreAutocomplete: React.FC<Props> = ({ value, onChange, options, placeholder, id }) => {
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const wrapRef = useRef<HTMLDivElement>(null)

  const matches = rank(options, value).slice(0, 8)
  // Nothing to suggest if the only match is exactly what's already typed.
  const show =
    open &&
    matches.length > 0 &&
    !(matches.length === 1 && matches[0].toLowerCase() === value.trim().toLowerCase())

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  // Reset the highlighted row whenever the suggestion list changes.
  useEffect(() => { setActive(0) }, [value])

  const choose = (s: string) => { onChange(s); setOpen(false) }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!show) return
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, matches.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)) }
    else if (e.key === 'Enter') { if (matches[active]) { e.preventDefault(); choose(matches[active]) } }
    else if (e.key === 'Escape') { setOpen(false) }
  }

  return (
    <div className="ac" ref={wrapRef}>
      <input
        id={id}
        placeholder={placeholder}
        value={value}
        autoComplete="off"
        onChange={(e) => { onChange(e.target.value); setOpen(true) }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      {show && (
        <div className="ac-menu">
          {matches.map((s, i) => (
            <div
              key={s}
              className={`ac-item${i === active ? ' on' : ''}`}
              // pointerdown (not click) so we select before the input blurs,
              // and preventDefault keeps focus in the input.
              onPointerDown={(e) => { e.preventDefault(); choose(s) }}
              onMouseEnter={() => setActive(i)}
            >
              {highlight(s, value)}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default StoreAutocomplete
